import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useLoginMutation,
  useRegisterInitMutation,
  useSmsSendMutation,
  useSmsVerifyMutation,
} from "@/services/api";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { useCountdownTimer } from "@/shared/hooks";
import { useCreatePinCodeMutation } from "@/services/api/entities/pinCode";
import { setRefreshToken, setToken, useAuth } from "@/services/auth";
import { appRouterPaths } from "@/services/navigation";
import type { RegisterInitRequest } from "@/services/api";
import { signUpActions } from "../sign-up-slice";
import { SignUpStep } from "../types";

const COOL_DOWN_TIME = 60;

export const useSignUpService = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { startTimer } = useCountdownTimer(COOL_DOWN_TIME, (count: number) =>
    dispatch(signUpActions.setResendCoolDown(count)),
  );
  const { accessPinHandler } = useAuth();

  const {
    currentStep,
    userData,
    userDetailsErrorMessage,
    phoneNumberError,
    resendCoolDown,
    smsErrorCount,
    smsSentCount,
    showCaptcha,
  } = useAppSelector((state) => state.signUp);

  const [initSignUp] = useRegisterInitMutation();
  const [sendSms] = useSmsSendMutation();
  const [verifySms] = useSmsVerifyMutation();
  const [createPinCode, { isLoading: pinFetching }] = useCreatePinCodeMutation();
  const [login] = useLoginMutation();

  const handleSubmitUserInfo = useCallback(
    (requestPayload: RegisterInitRequest) => {
      dispatch(signUpActions.setUserDetailsError(""));

      dispatch(signUpActions.setUserData(requestPayload));
      initSignUp(requestPayload)
        .unwrap()
        .then(() => {
          startTimer();
          dispatch(signUpActions.nextStep());
        })
        .catch((error) => {
          dispatch(signUpActions.setUserDetailsError(error.message));
        });
    },
    [initSignUp],
  );

  const handleVerifySms = useCallback(
    (code: string) => {
      verifySms({ username: userData.username, code })
        .unwrap()
        .then(() => {
          login({ username: userData.username, password: userData.password })
            .unwrap()
            .then((data) => {
              if (!data) return;
              dispatch(setToken(data?.token));
              dispatch(setRefreshToken(data?.refreshToken));
              dispatch(signUpActions.nextStep());
              dispatch(signUpActions.resetSmsErrorCount());
              dispatch(signUpActions.resetSmsSentCount());
            });
        })
        .catch((error) => {
          dispatch(signUpActions.setPhoneNumberError(error.message));
          dispatch(signUpActions.incrementSmsErrorCount());

          if (smsErrorCount + 1 >= 3) {
            dispatch(signUpActions.goToStep(SignUpStep.ExceededRegistrationAttempts));
            return;
          }

          dispatch(signUpActions.showCaptcha());
        });
    },
    [verifySms, userData.username, smsErrorCount],
  );

  const handleSubmitCaptcha = useCallback(() => {
    dispatch(signUpActions.hideCaptcha());
  }, []);

  const handleCreatePin = useCallback(
    (pin: string) => {
      createPinCode({ pin })
        .unwrap()
        .then(() => {
          accessPinHandler(pin);
        })
        .catch(() => {
          navigate(appRouterPaths.logIn);
          dispatch(signUpActions.clearState());
        });
    },
    [createPinCode],
  );

  const getNewSms = useCallback(() => {
    sendSms({ username: userData.username }).then(() => {
      startTimer();
      dispatch(signUpActions.incrementSmsSentCount());
    });
  }, [sendSms, userData.username]);

  return {
    state: {
      currentStep,
      userDetailsErrorMessage,
      phoneNumberError,
      resendCoolDown,
      smsSentCount,
      phoneNumber: userData.phoneNumber,
      showCaptcha,
      pinFetching,
    },
    methods: {
      handleSubmitUserInfo,
      handleVerifySms,
      getNewSms,
      handleSubmitCaptcha,
      handleCreatePin,
    },
  };
};
